// Pure helpers for the V3 plan, saved, notification and safety views (M21–M24, M27, M93–M97); no network, no storage.
import { own, text, lookup, participationState, isActivityCancelled, planEntries, accessFeatures, contactGroups } from "./memberDerive";
import { shortName } from "./v3People";

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
export const WEEKDAYS = [["Mo", "Montag"], ["Di", "Dienstag"], ["Mi", "Mittwoch"], ["Do", "Donnerstag"], ["Fr", "Freitag"], ["Sa", "Samstag"], ["So", "Sonntag"]];
const two = (n) => String(n).padStart(2, "0");
const clean = (v) => (typeof v === "string" ? v.trim() : "");
const idOf = (v) => (typeof v === "string" && v.trim() ? v : Number.isSafeInteger(v) ? String(v) : "");
const cpLen = (v) => Array.from(clean(v)).length;

function validDay(v) {
  const m = typeof v === "string" ? DAY.exec(v) : null;
  if (!m) return "";
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d ? v : "";
}

// YYYY-MM-DD of an instant in a time zone (the browser zone when none is given or it is unknown).
export function zonedDay(iso, timeZone) {
  const date = typeof iso === "string" ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const fmt = (tz) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
  try { return fmt(typeof timeZone === "string" && timeZone ? timeZone : undefined); } catch (_) { return fmt(undefined); }
}

export const todayKey = (now = Date.now(), timeZone) => zonedDay(new Date(now).toISOString(), timeZone);

export const STATE_TEXT = { confirmed: "Bestätigt", registered: "Eingetragen", requested: "Anfrage offen", waitlisted: "Warteliste", offered: "Platz angeboten", saved: "Nur gemerkt", activity_cancelled: "Abgesagt", cancelled: "Abgesagt", declined: "Abgelehnt", withdrawn: "Zurückgezogen", unknown: "Status unbekannt" };
export const stateText = (s, short) => (s === "saved" && short ? "Gemerkt" : lookup(STATE_TEXT, s) || STATE_TEXT.unknown);

const ACTIVE = ["confirmed", "registered", "requested"];
const ATTENDING = ["confirmed", "registered"];
const ENDED = ["declined", "withdrawn", "cancelled"];
const byWhen = (a, b) => (a.day || "9999").localeCompare(b.day || "9999") || (a.start || "99").localeCompare(b.start || "99");

function entry(src, kind, state) {
  return { id: src.id, kind, state, title: src.title, day: validDay(src.dateLocal) || zonedDay(src.date, src.timezone), start: CLOCK.test(src.start) ? src.start : "",
    location: clean(src.location), meetingPoint: clean(src.meetingPoint) };
}

const fromActivity = (a) => ({ id: idOf(own(a, "id")), title: text(own(a, "title")).trim() || "Aktivität", date: own(a, "date"), dateLocal: own(a, "date_local"), start: own(a, "start"),
  timezone: own(a, "timezone"), location: own(a, "location"), meetingPoint: own(a, "meeting_point") });

// Older Core without GET /member/plans: own participations from GET /member/activities in the normalizePlans shape.
export function plansFromActivities(d) {
  const list = own(d, "activities");
  if (!Array.isArray(list)) return null;
  const { upcoming, past } = planEntries(list.filter((a) => a && typeof a === "object"));
  return [...upcoming, ...past].map((a) => ({ ...fromActivity(a), activityId: idOf(own(a, "id")), state: participationState(a) })).filter((p) => p.activityId);
}

// One calendar/list entry per activity: own participations (normalizePlans) win over saved ones; saved stays "Nur gemerkt".
export function planItems(plans, saved) {
  const list = (Array.isArray(plans) ? plans : []).filter((p) => p && p.activityId).map((p) => entry({ ...p, id: p.activityId }, "plan", p.state));
  const ids = new Set(list.map((p) => p.id));
  for (const a of Array.isArray(saved) ? saved : []) {
    const src = fromActivity(a);
    if (!src.id || ids.has(src.id)) continue;
    ids.add(src.id);
    list.push(entry(src, "saved", isActivityCancelled(a) ? "activity_cancelled" : "saved"));
  }
  return list.sort(byWhen);
}

export const upcomingItems = (items, today) => items.filter((i) => i.day && i.day >= today && !ENDED.includes(i.state));
export const plannable = (items) => items.filter((i) => i.kind === "plan" && ACTIVE.includes(i.state));
export const attending = (items) => items.filter((i) => i.kind === "plan" && ATTENDING.includes(i.state));

export const monthOf = (day) => (validDay(day) ? { y: Number(day.slice(0, 4)), m: Number(day.slice(5, 7)) } : null);
export function firstMonth(items, today) {
  return monthOf((upcomingItems(items, today)[0] || {}).day) || monthOf(today) || { y: 2026, m: 1 };
}
export function shiftMonth({ y, m }, delta) {
  const t = new Date(Date.UTC(y, m - 1 + delta, 1));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
}
export const monthTitle = ({ y, m }) => `${MONTHS[m - 1]} ${y}`;

// Weeks Monday to Sunday; 0 marks a cell outside the month.
export function monthWeeks({ y, m }) {
  const lead = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const cells = [...Array(lead).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(0);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
}

const MARK = { confirmed: "confirmed", registered: "confirmed", requested: "requested", waitlisted: "requested", offered: "requested", saved: "saved", activity_cancelled: "cancelled", cancelled: "cancelled" };
const RANK = ["confirmed", "requested", "saved", "cancelled", "other"];

// Day number → entries and the strongest mark (confirmed before requested before saved).
export function monthMarks(items, { y, m }) {
  const prefix = `${y}-${two(m)}-`;
  const out = {};
  for (const i of items) {
    if (!i.day || !i.day.startsWith(prefix)) continue;
    const d = Number(i.day.slice(8));
    const mark = lookup(MARK, i.state) || "other";
    const cur = out[d] || { items: [], mark: "other" };
    out[d] = { items: [...cur.items, i], mark: RANK.indexOf(mark) < RANK.indexOf(cur.mark) ? mark : cur.mark };
  }
  return out;
}

export const dayTitle = (day) => (validDay(day) ? `${Number(day.slice(8))}. ${MONTHS[Number(day.slice(5, 7)) - 1]}` : "Termin offen");
export const dayDate = (day) => (validDay(day) ? `${day.slice(8)}.${day.slice(5, 7)}.${day.slice(0, 4)}` : "");
export const dayTime = (i) => (i.day ? [dayDate(i.day), i.start].filter(Boolean).join(" · ") : "Termin offen");
export const dayLabel = (d, { m }, list) => `${d}. ${MONTHS[m - 1]}: ${list.map((i) => `${i.title}, ${stateText(i.state)}`).join("; ")}`;
export const placeOf = (i) => [i.location, i.meetingPoint].filter(Boolean).join(" · ");
export const planOption = (i) => [i.title, dayDate(i.day)].filter(Boolean).join(" · ");

// M22: search title and public place; the city and access chips only narrow the already complete saved list.
export function filterSaved(list, { q = "", city = "", access = false } = {}) {
  const needle = clean(q).toLocaleLowerCase("de");
  const place = clean(city).toLocaleLowerCase("de");
  return (Array.isArray(list) ? list : []).filter((a) => {
    const where = [own(a, "location"), own(a, "meeting_point")].map(clean).join(" ").toLocaleLowerCase("de");
    if (needle && !`${text(own(a, "title"))} ${where}`.toLocaleLowerCase("de").includes(needle)) return false;
    if (place && !where.includes(place)) return false;
    return !access || accessFeatures(a).some((f) => f.state === "present");
  });
}

// M23: "Heute 09:42", "Gestern 18:10" or the full date; invalid values give "".
export function noticeTime(iso, now = Date.now(), timeZone) {
  const date = typeof iso === "string" ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  const parts = (d) => Object.fromEntries(new Intl.DateTimeFormat("de-CH", { timeZone, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(d).map((x) => [x.type, x.value]));
  const p = parts(date);
  const key = (q) => `${q.year}-${q.month}-${q.day}`;
  const clock = `${p.hour}:${p.minute}`;
  if (key(p) === key(parts(new Date(now)))) return `Heute ${clock}`;
  if (key(p) === key(parts(new Date(now - 86400000)))) return `Gestern ${clock}`;
  return `${p.day}.${p.month}.${p.year} · ${clock}`;
}
export const noticeContext = (n, now, timeZone) => [clean(n.body), noticeTime(n.createdAt, now, timeZone)].filter(Boolean).join(" · ") || "Ohne weitere Angaben";
export const firstNotice = (list) => (Array.isArray(list) ? list.find((n) => !n.read) || list[0] || null : null);

// Confirmed contacts (accepted direct conversations) are the only people who can be invited or travel along.
export function confirmedContacts(conversations) {
  const seen = new Set();
  return contactGroups(Array.isArray(conversations) ? conversations.filter((c) => c && typeof c === "object") : []).accepted.map((c) => {
    const id = idOf(own(own(c, "other"), "id"));
    if (!id || seen.has(id)) return null;
    seen.add(id);
    return { id, name: shortName(text(own(own(c, "other"), "name"))) || "Kontakt" };
  }).filter(Boolean);
}
export const firstName = (name) => clean(name).split(/\s+/)[0] || "";

// GET /member/trusted-contacts (proposed, D-84); a broken shape is null.
const TRUST_STATE = { eingeladen: "invited", "bestätigt": "confirmed", abgelehnt: "declined", beendet: "ended" };
export function normalizeTrusted(d) {
  const list = own(d, "trusted_contacts");
  if (!Array.isArray(list)) return null;
  return list.map((t) => {
    const who = own(t, "member");
    const v = { id: idOf(own(t, "id")), memberId: idOf(own(who, "id")), name: shortName(text(own(who, "name"))), state: lookup(TRUST_STATE, own(t, "state")) };
    return v.id && v.memberId && v.name && v.state ? v : null;
  }).filter(Boolean);
}
export function trustedText(list) {
  if (!Array.isArray(list)) return "Noch nicht verfügbar";
  const active = list.filter((t) => t.state === "confirmed" || t.state === "invited");
  return active.length ? active.map((t) => (t.state === "invited" ? `${t.name} · Einladung offen` : t.name)).join(", ") : "Noch niemand eingeladen";
}

export function inviteErrors(memberId, contacts, trusted) {
  if (!memberId) return { person: "Bitte wähle eine Person aus." };
  if (!contacts.some((c) => c.id === memberId)) return { person: "Nur bestätigte Kontakte können eingeladen werden." };
  if ((trusted || []).some((t) => t.memberId === memberId && (t.state === "confirmed" || t.state === "invited"))) return { person: "Diese Person ist bereits vertrauter Kontakt oder eingeladen." };
  return {};
}

export const MEETING_MAX = 120;
// M27: a plan needs an own participation, a public meeting point and a clock time; companions only from confirmed contacts.
export function planErrors({ termin, meetingPoint, time, companion }, items, contacts) {
  const e = {};
  if (!plannable(items).some((i) => i.id === termin)) e.termin = "Bitte wähle einen Termin, an dem du teilnimmst oder angefragt hast.";
  if (!cpLen(meetingPoint)) e.meetingPoint = "Bitte gib einen öffentlichen Treffpunkt an.";
  else if (cpLen(meetingPoint) > MEETING_MAX) e.meetingPoint = `Der Treffpunkt darf höchstens ${MEETING_MAX} Zeichen lang sein.`;
  if (!CLOCK.test(typeof time === "string" ? time : "")) e.time = "Bitte gib eine Uhrzeit im Format HH:MM an.";
  if (companion && !contacts.some((c) => c.id === companion)) e.companion = "Nur bestätigte Kontakte können eingeladen werden.";
  return e;
}

export const CHECKIN_CHOICES = ["arrived", "plan_changed"];
export function checkinErrors({ termin, choice }, items) {
  const e = {};
  if (!attending(items).some((i) => i.id === termin)) e.termin = "Es gibt keinen bestätigten Termin für eine Rückmeldung.";
  if (!CHECKIN_CHOICES.includes(choice)) e.choice = "Bitte wähle deine Rückmeldung.";
  return e;
}

// GET /member/plans/{activity_id}/checklist (proposed, D-81).
export const CHECKLIST = [["meeting_point", "Treffpunkt lesen"], ["access", "Zugangsfrage klären"], ["return", "Rückweg planen"]];
export function normalizeChecklist(d) {
  const c = own(d, "checklist");
  const items = own(c, "items");
  if (!Array.isArray(items)) return null;
  const done = {};
  for (const it of items) if (CHECKLIST.some(([k]) => k === own(it, "key")) && own(it, "done") === true) done[it.key] = true;
  return { done, share: own(c, "share_with_plan_members") === true };
}

// GET /member/plans/{activity_id}/travel (proposed, D-80).
export function normalizeTravel(d) {
  const t = own(d, "travel");
  if (!t || typeof t !== "object" || Array.isArray(t)) return null;
  const time = own(t, "meet_time");
  return { meetingPoint: clean(own(t, "meeting_point_public")), time: CLOCK.test(time) ? time : "", returnNote: clean(own(t, "return_note")) };
}
